import axios from 'axios';
import ErrorService from './error-service';
import ConfigService from './config';
import { EventBus } from './event-bus-service';

/**
 * @typedef {import("axios").AxiosPromise} AxiosPromise
 * @typedef {import("axios").AxiosResponse} AxiosResponse
 * @typedef {import("axios").AxiosError} AxiosError
 * @typedef {import("axios").AxiosRequestConfig} AxiosRequestConfig
 */

/**
 * Base request class for all requests
 */
export default class RequestService {
  /**
   * class constructor method
   */
  constructor() {
    /**
     * Keep reference here to config service so we don'tave to import it in every service
     */
    this.$config = ConfigService;
    /**
     * Base api endpoint
     * @type {string}
     */
    this.baseUrl = APP_SETTINGS.path.apiUrl;
    /**
     * Api Endpoint
     * @type {null|String}
     */
    this.endpoint = null;
    /**
     * Request method, default post
     * @type {string}
     */
    this.method = 'post';
    /**
     * Request
     * @type {AxiosPromise | null}
     */
    this.request = null;
    /**
     * Response
     * @type {{}}
     */
    this.response = null;
    /**
     * Response type, possible values are:
     * arraybuffer, blob, document, json, text, stream
     * @type {string}
     */
    this.responseType = 'json';
    /**
     * Request headers, default is empty object
     * @type {{}}
     */
    this.headers = {};
    /**
     * Post parameters
     * @type {{}}
     */
    this.postParams = {};
    /**
     * Get parameters
     * @type {{}}
     */
    this.getParams = {};
    /**
     * Cache time - default is 2 min.
     * @type {number}
     */
    this.cacheTime = 1000 * 60 * 2;
    /**
     * Should we cache request or not - default is false
     * @type {boolean}
     */
    this.cacheResponse = false;
    /**
     * Store logging data from requests
     * @type {Array}
     */
    this.logInfo = [];
    /**
     * Should we use logging or not
     * @type {boolean}
     */
    this.logData = true;

    /**
     * Abort current request
     * @type {null}
     */
    this.abort = () => {
    };

    // this.addHeader('ExternalUserID', APP_SETTINGS.player.id);
    // this.addHeader('Token', encodeURI(APP_SETTINGS.player.token));
    // this.addHeader('TimeOffset', dateFilterService.utcTimeOffset());
    // this.addHeader('Culture', APP_SETTINGS.player.language);
    // this.addHeader('Provider', '0');
  }

  /**
   * Cache object - will hold responses that will be cached on client side
   * @type {{}}
   */
  static cache = {};

  /**
   * Cache mock responses
   * @type {}
   */
  static mockCache = {};

  /**
   * Add header to request
   * @param key
   * @param value
   * @returns {boolean}
   */
  addHeader(key, value) {
    if (!key || !value) {
      return false;
    }
    this.headers[key] = value;
  }

  /**
   * Return current date in format yyyy-mm-dd
   * @param offset
   * @returns {string}
   */
  currentDate(offset) {
    const now = new Date();
    let day = now.getDate();
    if (offset) {
      day = now.getDate() + offset;
    }
    return `${now.getFullYear()}-${now.getMonth() + 1}-${day}`;
  }

  /**
   * Add post parameter
   * @param key
   * @param value
   */
  addPostParam(key, value) {
    this.postParams[key] = value;
  }

  addPostParamValue(value) {
    this.postParams = value;
  }

  /**
   * Add get parameter
   * @param key
   * @param value
   */
  addGetParam(key, value) {
    this.getParams[key] = value;
  }

  /**
   * Execute mock request instead real one on admin side
   */
  executeMockRequest() {
    if (typeof ConfigService.widgetData[this.endpoint] !== 'undefined') {
      if (typeof RequestService.mockCache[this.endpoint] !== 'undefined') {
        return RequestService.mockCache[this.endpoint].data;
      }
      let config = {
        method: 'get',
        url: `/static/mock-data/${ConfigService.widgetData[this.endpoint]}`,
        responseType: 'json',
      };
      let response = axios(config);
      RequestService.mockCache[this.endpoint] = { data: response };
      return response;
    }
    return false;
  }

  /**
   * Execute request
   * @param {{(resp: AxiosResponse): void}} [success]
   * @param {{(error: AxiosError): void}} [error]
   * @returns {AxiosPromise|undefined}
   */
  execute(success, error) {
    /**
     * If this method is running in widget on admin side
     * then use mock data instead of real data
     */
    if (process.env.VUE_APP_IS_ADMIN_CUSTOMIZER) {
      return this.executeMockRequest();
    }
    /**
     * Return cached response if exists
     */
    let cachedRequestResponse = this.getCached(this.endpoint);
    if (cachedRequestResponse !== false) {
      success(cachedRequestResponse.response);
      return;
    }
    /**
     * Execute real request
     */
    this.request = axios(this.getRequestConfig());
    this.request
      .then((resp) => {
        if (typeof success === 'function') {
          if (this.cacheResponse) {
            this.setCacheKey(this.endpoint, resp);
          }
          success(resp);
        }
      })
      .catch((err) => {
        ErrorService.logError(err, err.toString());
        // on agent display error for session expired. 401 will always return unautrozed
        if (process.env.VUE_APP_IS_AGENT && err.response && err.response.status === 401) {
          EventBus.$emit('modalMessage', {
            showModal: true,
            messageType: 'error',
            translate: true,
            messageText: 'A.sessionExpired',
            modalSize: 'md',
            modalPossition: 'start',
          });
          // reset token and id as the session is not valid anymore
          APP_SETTINGS.player.token = '';
          APP_SETTINGS.player.id = '';
        }
        if (typeof error === 'function') {
          error(err);
        }
    });
    return this.request;
  }

  /**
   * Build config for request
   * @returns {{method: string, url: String, baseURL: string, responseType: string, headers: {}}}
   */
  getRequestConfig() {
    const CancelToken = axios.CancelToken;
    let config = {
      method: this.method,
      url: this.endpoint,
      baseURL: this.baseUrl,
      responseType: this.responseType,
      cancelToken: new CancelToken((c) => {
        this.abort = c;
      }),
      headers: this.headers,
    };
    if (this.method === 'post' || this.method === 'put' || this.method === 'patch') {
      config.data = this.postParams;
    } else if (this.method === 'get') {
      config.params = this.getParams;
    }
    return config;
  }

  /**
   * Save response in cache
   * @param key
   * @param response
   */
  setCacheKey(key, response) {
    RequestService.cache[key] = {
      key,
      data: Object.assign({}, this.postParams, this.getParams),
      response,
      time: Date.now(),
    };
  }

  /**
   * Get cached response
   * @param key
   * @param data
   */
  getCached(key) {
    if (!key) {
      return false;
    }
    let data = Object.assign({}, this.postParams, this.getParams);
    let cachedResponse = typeof RequestService.cache[key] !== 'undefined' ? RequestService.cache[key] : false;
    if (cachedResponse !== false) {
      if (Date.now() - cachedResponse.time > this.cacheTime ||
        JSON.stringify(cachedResponse.data) !== JSON.stringify(data)) {
          // caching expired
          this.removeCacheKey(key);
          return false;
        }
      return cachedResponse;
    }
    return false;
  }

  /**
   * Remove item from cache
   * @param key
   */
  removeCacheKey(key) {
    if (typeof RequestService.cache[key] !== 'undefined') {
      delete RequestService.cache[key];
    }
  }
}
